import type { Theme } from '@material-ui/core';
import {
  AppBar,
  Backdrop,
  Badge,
  ClickAwayListener,
  createStyles,
  Toolbar,
} from '@material-ui/core';
import Snackbar from '@material-ui/core/Snackbar';
import { makeStyles } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import {
  CustomPalette,
  Icons,
  MaterialPalette,
  Tooltip,
  Typography,
} from '@nutrien/cxp-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import postal from 'postal';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Subscription } from 'rxjs';

import { useMst } from '../../mobx-models/Root';
import useNotificationData from '../../rxdb/notification/useNotificationData';
import { useRxDB } from '../../rxdb/useRxDB';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { useAIMSHeartbeat } from '../../utilities/hooks/useAIMSHeartbeat';
import { useFeatureFlags } from '../../utilities/useFeatureFlag';
import type { FeatureFlags } from '../../types/featureFlags';
import {
  getLocalStorageSelectedDisciplineId,
  getLocalStorageSelectedSiteId,
  getValueFromLocalStorage,
  setValueinLocalStorage,
} from '../../utilities/utilityFunctions';
import NotificationDialog from '../Notification';
import SiteDisciplinePopUp from '../SiteDisciplinePopUp';
import SignatureAndStamp from '../UserSignatureAndStamp/SignatureAndStamp';
import CustomAvatarIcon from './CustomAvatarIcon';
import LogoutModal from './LogoutModal';
import UserMenu from './UserMenu';

const useStyles = makeStyles(() =>
  createStyles({
    appBar: {
      backgroundColor: CustomPalette.elevation.dp4,
      boxShadow: '0px 0px 16px #000000',
    },
    site: {
      justifyContent: 'right',
    },
    toolbarContent: {
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      width: '100%',
      alignItems: 'center',
    },
    storageContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '8px',
    },
    discipline: {
      justifySelf: 'center',
      marginLeft: '24px',
    },
    actions: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '24px',
      justifySelf: 'end',
    },
    nameGrid: {
      cursor: 'pointer',
      display: 'flex',
      marginRight: 24,
    },
    chevDownFeather: {
      cursor: 'pointer',
    },
    dateModifyGrid: {
      position: 'inherit',
    },
    dateModifyvalue: {
      color: 'rgba(255, 255, 255, 0.6)',
    },
    iconPos: {
      display: 'flex',
      alignSelf: 'center',
    },
    refreshIcon: {
      animation: '$icon-spin 2s infinite linear',
    },
    icon: {
      color: 'white',
    },
    '@-webkit-keyframes icon-spin': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '100%': {
        transform: 'rotate(-359deg)',
      },
    },
    '@keyframes icon-spin': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '100%': {
        transform: 'rotate(-359deg)',
      },
    },
    iconStyle: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: 8,
    },
    muiAlert: {
      width: 'maxContent',
      display: 'inline-flex',
    },
    storageSpace: {
      color: 'rgba(255, 255, 255, 0.6)',
    },
    alertTriangleIcon: {
      display: 'flex',
    },
  }),
);

const useMuiStyles = makeStyles((theme: Theme) => ({
  backdrop: { color: MaterialPalette.common.white, zIndex: theme.zIndex.drawer + 1 },
}));

const CustomAppBar: React.FC = () => {
  const classes = useStyles();
  const classesSX = useMuiStyles();
  const { syncData, SiteDiscipline, primaryContext, configCatFlag, HandleGesture } = useMst();
  const { db } = useRxDB();
  const featureFlag: FeatureFlags = useFeatureFlags();
  const [aimsDataSync, setAimsDataSync] = useState({ message: '', allowSync: true });

  const localStorageSelectedSiteId = getLocalStorageSelectedSiteId();
  const localStorageSelectedDisciplineId = getLocalStorageSelectedDisciplineId();

  const data = SiteDiscipline.getSiteDisciplineData;
  const online = useOnlineStatus();
  const history = useHistory();
  const { unreadNotificationList } = useNotificationData(
    SiteDiscipline.siteId,
    SiteDiscipline.disciplineId,
  );
  const { sendAIMSHeartbeat } = useAIMSHeartbeat();

  const [logoutOpen, setLogoutOpen] = useState<boolean>(false);
  const [dialog, setDialog] = useState<boolean>(false);
  const [closeMessage, setCloseMessage] = useState<boolean>(false);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLDivElement | null>(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLDivElement | null>(null);
  const [signatureDialogAnchorEl, setSignatureDialogAnchorEl] = useState<HTMLButtonElement | null>(
    null,
  );
  const [openStorageLimitMsg, setOpenStorageLimitMsg] = useState<boolean>(false);
  const [storageLimitWarning, setStorageLimitWarning] = useState(false);
  const [storageDetails, setStorageDetails] = useState({ used: '', totalSize: '' });
  const [syncCount, setSyncCount] = useState<number>(0);
  const [lastSyncTime, setLastSyncTime] = useState<string>('');
  const DEBUG_RXDB = localStorage.getItem('DEBUG_RXDB') === 'true';
  const [warningTooltipOpen, setWarningTooltipOpen] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('initialSync') === 'false') {
      syncData.setWorpackageSync(true);
      syncData.setMasterDataSync(true);
      syncData.setInspectionFileSync(true);
      syncData.setDeficiencyFileSync(true);
      syncData.setPreviousReportSync(true);
      syncData.setComponentTypeListSync(true);
      syncData.setRemedialActionListSync(true);
      syncData.setPrimaryCauseOfFailureListSync(true);
      syncData.setConditionRatingListSync(true);
      syncData.setUrgencyListSync(true);
      syncData.setOperationalDowntimeListSync(true);
      syncData.setTrafficSafetyListSync(true);
    }
  }, [localStorage.getItem('initialSync')]);

  useEffect(() => {
    const dateFormat = 'DD-MM-YYYY hh:mm A';
    const syncTime = HandleGesture.lastManualSync;
    if (syncTime) {
      setLastSyncTime(dayjs(new Date(Number(syncTime))).format(dateFormat));
    }
  }, [HandleGesture.lastManualSync]);

  useEffect(() => {
    if (
      HandleGesture.isManualSyncClicked &&
      !HandleGesture.errorInManualSync &&
      !syncData.isSyncing
    ) {
      HandleGesture.setManualSync(Date.now());
      HandleGesture.setManualSyncClicked(false);
      HandleGesture.setErrorInManualSync(false);
    }
  }, [HandleGesture.isManualSyncClicked, syncData.isSyncing]);

  useEffect(() => {
    if (Number(getValueFromLocalStorage('totalFilesToDownload')) && syncData?.fixTotalCount === 0) {
      setValueinLocalStorage('assetFilesCount', 0?.toString());
      syncData.setTotalFixCount(Number(getValueFromLocalStorage('totalFilesToDownload')));
    }
  }, [syncData?.fixTotalCount, getValueFromLocalStorage('totalFilesToDownload')]);

  useEffect(() => {
    if (
      SiteDiscipline.siteId === Number(localStorageSelectedSiteId) ||
      SiteDiscipline.disciplineId === Number(localStorageSelectedDisciplineId)
    ) {
      if (syncData?.totalDownloadCount) {
        setValueinLocalStorage('totalFilesDownloaded', syncData?.totalDownloadCount?.toString());
      }
    }
  }, [syncData?.totalDownloadCount, getValueFromLocalStorage('totalFilesDownloaded')]);

  useEffect(() => {
    if (syncData.fixTotalCount !== syncData.totalDownloadCount && online) {
      setCloseMessage(true);
      if (syncData.totalDownloadCount > syncData.fixTotalCount) {
        setValueinLocalStorage('totalFilesDownloaded', syncData.fixTotalCount?.toString());
        setValueinLocalStorage('assetFilesCount', 0?.toString());
      }
    } else {
      setCloseMessage(false);
      setValueinLocalStorage('assetFilesCount', 0?.toString());
      localStorage.setItem('initialSync', 'false');
    }
  }, [
    syncData.totalDownloadCount,
    syncData.fixTotalCount,
    online,
    SiteDiscipline.site,
    SiteDiscipline.discipline,
  ]);

  const openUserMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    setUserMenuAnchorEl(event.currentTarget);
  };

  const openNotification = (event: React.MouseEvent<HTMLDivElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
  };

  const handleSignatureDialogOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setSignatureDialogAnchorEl(event.currentTarget);
    handleUserMenuClose();
  };

  const handleSignatureDialogClose = () => {
    setSignatureDialogAnchorEl(null);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const onLogoutSelected = () => {
    setLogoutOpen(true);
  };

  const onLogout = async () => {
    await sendAIMSHeartbeat({ logoutTime: dayjs(), comment: { location: 'AppBar' } });
    history.push('/logout');
  };

  const openDialog = () => {
    if (online) setDialog(true);
  };

  const syncOnce = async () => {
    try {
      db?.startReplication({ isShowSync: true });
      HandleGesture.setManualSyncClicked(true);
    } catch (error) {
      console.error('error', error); // eslint-disable-line no-console
      HandleGesture.setManualSyncClicked(false);
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: AppBar.tsx ~ line 204 ~ syncOnce ~');
    }
  };

  const increaseCount = () => setSyncCount(prev => prev + 1);

  const callOnlineServiceAndCompleteSync = async () => {
    const siteId = SiteDiscipline.siteId;
    const disciplineId = SiteDiscipline.disciplineId;
    if (aimsDataSync.allowSync && siteId && disciplineId && online)
      await Promise.all([
        primaryContext.getwpFilesFromMobx(siteId, disciplineId),
        primaryContext.getWorkPackageFromMobx(siteId, disciplineId),
        primaryContext.getInspectionListFromMobx(siteId, disciplineId),
      ]);
    syncData.setIsSyncing(false);
    setSyncCount(0);
  };

  const DEBUG_FILES = localStorage.getItem('DEBUG_FILES') === 'true';

  if (DEBUG_FILES) {
    /* eslint-disable */
    console.log('filesDownloaded', syncData?.totalDownloadCount);
    console.log('filesToDownload', syncData?.fixTotalCount);
    /* eslint-enable */
  }

  const isSpinning = useMemo(() => {
    if (!online) return false;
    return localStorage.getItem('initialSync') === 'true' || syncData.isSyncing;
  }, [localStorage.getItem('initialSync') === 'true', online, syncData.isSyncing]);

  navigator.storage.estimate().then(estimate => {
    if (estimate?.usage && estimate?.quota) {
      const browserStorage = (estimate?.usage / estimate?.quota) * 100;
      if (browserStorage > 99) {
        setOpenStorageLimitMsg(true);
      }
      if (browserStorage > 80) {
        setStorageLimitWarning(true);
      }
      if ((estimate?.usage / 1000000 / 1024).toFixed(2) !== storageDetails.used) {
        setStorageDetails({
          used: (estimate?.usage / 1000000 / 1024).toFixed(2),
          totalSize: (estimate?.quota / 1000000 / 1024).toFixed(2),
        });
      }
    }
  });

  useEffect(() => {
    if (featureFlag && featureFlag?.aimsDataSyncIsActive) {
      const { aimsDataSyncIsActive, aimsDataSyncMessage } = featureFlag;
      setAimsDataSync({
        message: String(aimsDataSyncMessage ?? ''),
        allowSync: aimsDataSyncIsActive as boolean,
      });
    }
  }, [featureFlag]);

  useEffect(() => {
    if (DEBUG_RXDB)
      console.log({
        completed: syncCount,
        total: db?.syncStates?.length,
      });
    if (syncCount >= db?.syncStates?.length) {
      callOnlineServiceAndCompleteSync();
    }
  }, [syncCount, db?.syncStates]);

  useEffect(() => {
    const subArray: Subscription[] = [];
    const postalSub = postal.subscribe({
      channel: 'sync',
      topic: 'sync.show',
      callback: () => {
        // reset initial sync flags
        SiteDiscipline.setSiteDisciplineUpdateInitialSync(false);
        syncData.setIsSyncing(true);
        setSyncCount(0);

        // check for initial replication
        db?.syncStates?.forEach(state => {
          const sub = state.initialReplicationComplete$.subscribe(
            (initialReplicationComplete: boolean) => {
              if (initialReplicationComplete === true) {
                if (DEBUG_RXDB) console.log('Collection Name: ', state.collection.name);
                increaseCount();
              }
            },
          );
          subArray.push(sub);
        });
      },
    });

    return () => {
      postalSub.unsubscribe();
      subArray.forEach(sub => sub.unsubscribe());
    };
  }, []);

  return (
    <>
      {(localStorage.getItem('initialSync') === 'true' || syncData.isSyncing) && (
        <>
          <Snackbar open={closeMessage || syncData.isSyncing} autoHideDuration={20000}>
            <Alert icon={false} variant="filled" color="success" className={classes.muiAlert}>
              {syncData.totalDownloadCount > 0 &&
              syncData.fixTotalCount > 0 &&
              syncData.totalDownloadCount < syncData.fixTotalCount
                ? `Syncing In Progress: ${Math.floor(
                    (Math.round(syncData.totalDownloadCount) / Math.round(syncData.fixTotalCount)) *
                      100,
                  )}%. Total Files Downloaded: ${syncData?.totalDownloadCount}/${
                    syncData?.fixTotalCount
                  }`
                : 'Syncing In Progress'}
            </Alert>
          </Snackbar>
        </>
      )}

      {openStorageLimitMsg && (
        <Snackbar open={openStorageLimitMsg}>
          <Alert
            icon={false}
            variant="filled"
            color="error"
            className={classes.muiAlert}
            onClose={() => setOpenStorageLimitMsg(false)}
          >
            Your Browser Memory is full.
          </Alert>
        </Snackbar>
      )}

      <AppBar position="static" className={classes.appBar}>
        {localStorage.getItem('initialSync') === 'true' &&
          syncData.fixTotalCount !== syncData.totalDownloadCount &&
          online && <Backdrop open className={classesSX.backdrop} />}
        <Toolbar>
          <div className={classes.toolbarContent}>
            <div className={classes.storageContainer}>
              <Typography variant="h6" className={classes.storageSpace}>
                Memory {storageDetails.used} GB / {storageDetails.totalSize} GB
              </Typography>
              {storageLimitWarning && (
                <ClickAwayListener onClickAway={() => setWarningTooltipOpen(false)}>
                  <div>
                    <Tooltip
                      PopperProps={{
                        disablePortal: true,
                      }}
                      onClose={() => setWarningTooltipOpen(false)}
                      open={warningTooltipOpen}
                      disableFocusListener
                      disableHoverListener
                      disableTouchListener
                      title={
                        <Typography variant="body2">
                          {openStorageLimitMsg
                            ? 'Device memory limit reached. Please free up memory on the device to continue using the application.'
                            : 'Device memory limit almost reached. Please free up memory on the device to avoid performance issues.'}
                        </Typography>
                      }
                    >
                      <Icons.AlertTriangleFeather
                        stroke={MaterialPalette.warning.main}
                        onClick={() => setWarningTooltipOpen(!warningTooltipOpen)}
                        className={classes.alertTriangleIcon}
                      />
                    </Tooltip>
                  </div>
                </ClickAwayListener>
              )}
            </div>
            <div className={classes.discipline}>
              <div
                onClick={openDialog}
                className={classes.nameGrid}
                data-testid="siteDisciplineIcon"
              >
                <Typography variant="h5" color="textPrimary">
                  {`${data.site} - ${data.discipline}`}
                </Typography>
                <Typography className={classes.iconStyle}>
                  <Icons.ChevronDownFeather
                    stroke="white"
                    onClick={openDialog}
                    className={classes.chevDownFeather}
                  />
                </Typography>
              </div>
            </div>
            <div className={classes.actions}>
              {lastSyncTime && (
                <div className={classes.dateModifyGrid}>
                  <Typography variant="h6" className={classes.dateModifyvalue}>
                    Last Manual Sync : {lastSyncTime}
                  </Typography>
                </div>
              )}
              <div className={classes.iconPos} onClick={openNotification}>
                <Badge badgeContent={unreadNotificationList?.length} max={100} color="error">
                  <Icons.BellIcon color="primary" size="small" data-testid="bellIcon" />
                </Badge>
              </div>
              <div
                className={classes.iconPos}
                onClick={() => {
                  if (online && !syncData.isSyncing && aimsDataSync?.allowSync) {
                    syncOnce();
                  }
                }}
              >
                <Badge>
                  <Icons.RefreshCcwFeather
                    color="primary"
                    size="small"
                    className={isSpinning ? classes.refreshIcon : ''}
                    data-testid="syncIcon"
                  />
                </Badge>
              </div>
              <div className={classes.iconPos}>
                {online ? (
                  <Icons.WifiFeather color="primary" size="small" />
                ) : (
                  <Icons.WifiOffFeather color="error" size="small" />
                )}
              </div>
              <div onClick={openUserMenu} className={classes.iconPos} data-testid="usermenubutton">
                <Badge color="error">
                  <CustomAvatarIcon />
                </Badge>
              </div>
            </div>
          </div>
          <UserMenu
            id="user-menu"
            open={Boolean(userMenuAnchorEl)}
            anchorEl={userMenuAnchorEl}
            onClose={handleUserMenuClose}
            onLogout={onLogoutSelected}
            onSignatureAndStampClick={handleSignatureDialogOpen}
          />
          <LogoutModal
            open={logoutOpen}
            onCancel={() => setLogoutOpen(false)}
            onLogout={onLogout}
            disableEnforceFocus={false}
          />
          <NotificationDialog
            open={Boolean(notificationAnchorEl)}
            anchorEl={notificationAnchorEl}
            onClose={handleNotificationClose}
          />
          <SignatureAndStamp
            id="signature-and-stamp-dialog"
            open={Boolean(signatureDialogAnchorEl)}
            anchorEl={signatureDialogAnchorEl}
            onClose={handleSignatureDialogClose}
          />
        </Toolbar>
        {configCatFlag?.blueBanner?.display === true && (
          <Alert severity="info">{configCatFlag?.blueBanner?.message}</Alert>
        )}
        {configCatFlag?.redBanner?.display === true && (
          <Alert severity="error">{configCatFlag?.redBanner?.message}</Alert>
        )}
      </AppBar>

      {dialog && <SiteDisciplinePopUp open={dialog} onClose={() => setDialog(false)} />}
    </>
  );
};

export default observer(CustomAppBar);
